/**
 * Footer Service
 * Fetches footer content from Application Translator API.
 */

import api from './api';
import { environment } from '../environments/environment';

export interface FooterData {
    footerText: string;
    copyRight: string;
    footerText1: string;
    footerLinkText1: string;
    footerLinkURL1: string;
    footerLinkText2: string;
    footerLinkURL2: string;
}

export interface FooterContent extends FooterData {
    disclaimerText?: string;
}

interface ApplicationTranslatorResponse {
    disclaimer?: {
        aiContentDisclaimer?: string;
    };
    footer?: FooterData;
}

const SAFE_FOOTER_PROTOCOLS = new Set(['http:', 'https:', 'mailto:', 'tel:']);

const APPLICATION_TRANSLATOR_URL = `${(
    (import.meta.env.VITE_apiBaseUrl as string | undefined)?.trim() || environment.APIServiceUrl
).replace(/\/$/, '')}/ApplicationTranslator`;

type FooterPayload = Array<{ key: string; value: string }>;

function extractHref(value: string): string {
    if (!value) {
        return '';
    }

    const quotedHref = value.match(/href\s*=\s*(['"])(.*?)\1/i);
    if (quotedHref?.[2]) {
        return quotedHref[2];
    }

    const unquotedHref = value.match(/href\s*=\s*([^\s>]+)/i);
    return unquotedHref?.[1] ?? value;
}

function sanitizeFooterUrl(value: string): string {
    const href = extractHref(value).trim();

    if (!href) {
        return '';
    }

    if (
        href.startsWith('/') ||
        href.startsWith('./') ||
        href.startsWith('../') ||
        href.startsWith('#')
    ) {
        return href;
    }

    if (!/^[a-zA-Z][a-zA-Z\d+.-]*:/.test(href)) {
        return '';
    }

    try {
        const parsedUrl = new URL(href);
        return SAFE_FOOTER_PROTOCOLS.has(parsedUrl.protocol) ? href : '';
    } catch {
        return '';
    }
}

function isFooterData(value: unknown): value is FooterData {
    if (!value || typeof value !== 'object') {
        return false;
    }

    const footer = value as Record<string, unknown>;
    return (
        typeof footer.footerText === 'string' &&
        typeof footer.copyRight === 'string' &&
        typeof footer.footerText1 === 'string' &&
        typeof footer.footerLinkText1 === 'string' &&
        typeof footer.footerLinkURL1 === 'string' &&
        typeof footer.footerLinkText2 === 'string' &&
        typeof footer.footerLinkURL2 === 'string'
    );
}


export async function fetchFooterContent(
    language = 'en-US',
    payload: FooterPayload,
): Promise<FooterContent> {
    let lastError: unknown = null;

    try {
        const data = await api.fetch<ApplicationTranslatorResponse>(
            `${APPLICATION_TRANSLATOR_URL}/${encodeURIComponent(language)}`,
            {
                method: 'POST',
                body: JSON.stringify(payload),
            },
        );

        if (!isFooterData(data.footer)) {
            throw new Error(
                'Footer API returned an invalid footer payload.',
            );
        }

        const footer = data.footer;

        return {
            ...footer,
            disclaimerText: data.disclaimer?.aiContentDisclaimer,
            footerLinkURL1: sanitizeFooterUrl(footer.footerLinkURL1),
            footerLinkURL2: sanitizeFooterUrl(footer.footerLinkURL2),
        };
    } catch (error) {
        lastError = error;
    }


    throw lastError instanceof Error
        ? lastError
        : new Error('Failed to fetch footer content.');
}
